import { z } from 'zod';

export const suspendUserSchema = z.object({
  reason: z.string().min(10, 'Reason must be at least 10 characters'),
});
export type SuspendUserInput = z.infer<typeof suspendUserSchema>;

export const changeRoleSchema = z.object({
  role: z.enum(['USER', 'EDITOR', 'ADMIN', 'SUPER_ADMIN']),
});
export type ChangeRoleInput = z.infer<typeof changeRoleSchema>;

export const refundPaymentSchema = z
  .object({
    // `amount` is in major units (rupees); `maxAmount` is the payment total in paise.
    amount: z.coerce.number().positive().optional(),
    reason: z.string().min(10, 'Reason must be at least 10 characters'),
    category: z.enum(['SERVICE_FAULT', 'PAYMENT_ISSUE', 'PACK_REFUND']),
    overrideWindowReason: z.string().max(500).optional(),
    maxAmount: z.number().int().positive(),
  })
  .refine((d) => d.amount === undefined || Math.abs(d.amount * 100 - Math.round(d.amount * 100)) < 1e-6, {
    path: ['amount'],
    message: 'Use at most 2 decimal places',
  })
  .refine((d) => d.amount === undefined || Math.round(d.amount * 100) >= 1, {
    path: ['amount'],
    message: 'Refund amount must be at least 0.01',
  })
  .refine((d) => d.amount === undefined || Math.round(d.amount * 100) <= d.maxAmount, {
    path: ['amount'],
    message: 'Refund amount cannot exceed payment amount',
  });
export type RefundPaymentInput = z.infer<typeof refundPaymentSchema>;

export const bulkSuspendSchema = z.object({
  reason: z.string().min(10, 'Reason must be at least 10 characters'),
});
export type BulkSuspendInput = z.infer<typeof bulkSuspendSchema>;

// Grant/revoke beta publish credits. `amount` is a count (not money); reason is
// optional audit context. Same shape for both actions.
export const creditsSchema = z.object({
  amount: z.coerce
    .number()
    .int('Enter a whole number')
    .min(1, 'Enter at least 1 credit')
    .max(1000, 'Maximum 1000 at a time'),
  reason: z.string().max(500).optional(),
});
export type CreditsInput = z.infer<typeof creditsSchema>;

// Rupee-wallet correction. `amount` is MONEY (₹ = credits) and the reason is REQUIRED: it is the audit trail for a
// hand-edited balance (mirrors the backend WalletAdjustDto).
export const walletAdjustSchema = z.object({
  amount: z.coerce.number().int('Enter a whole number of rupees').min(1, 'Enter at least ₹1').max(100000, 'Maximum ₹1,00,000 at a time'),
  reason: z.string().min(10, 'Reason must be at least 10 characters').max(500),
});
export type WalletAdjustInput = z.infer<typeof walletAdjustSchema>;
